import { useState } from "react";
import { CalendarRange, CheckSquare, HardHat, Truck, Loader2, Trash2, ShieldAlert, Search } from "lucide-react";
import { Loading, Empty, Field, NativeSelect, Badge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { eur, fmtDate, SITE_STATUS } from "../../lib/format";
import { cn } from "../../lib/utils";
import { ShiftFields } from "../../components/ShiftFields";
import { EditTimeButton } from "../../components/AssignmentEdit";

const rowTone = (r, checked) => {
  if (!r.available) return "border-red-900/70 bg-red-950/30 hover:bg-red-950/50";
  return checked ? "border-amber-500 bg-amber-500/10" : "border-border hover:bg-accent/40";
};

function ListState({ valid, loading, empty, children }) {
  if (!valid) return <Empty title="Seleziona cantiere e periodo" />;
  if (loading) return <Loading label="Calcolo disponibilità…" />;
  if (empty) return <Empty title="Nessun elemento" />;
  return children;
}

function StatusBadge({ r, type }) {
  if (r.available) return <Badge className="bg-emerald-950 border-emerald-600 text-emerald-200" testid={`plan-${type}-status-${r.id}`}>🟢 DISPONIBILE</Badge>;
  return <Badge className="bg-red-950 border-red-600 text-red-200" testid={`plan-${type}-status-${r.id}`}>🔴 NON DISPONIBILE</Badge>;
}

function ResourceRow({ r, type, checked, onToggle, force, onShowConflict }) {
  const selectable = r.available || force;
  return (
    <div onClick={() => (selectable ? onToggle(r.id) : onShowConflict(r))}
      className={cn("flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors min-h-[60px]", rowTone(r, checked))}
      data-testid={`plan-${type}-${r.id}`}>
      <Checkbox checked={checked} disabled={!selectable} onClick={(e) => e.stopPropagation()} onCheckedChange={() => selectable && onToggle(r.id)} className="h-5 w-5" data-testid={`plan-${type}-checkbox-${r.id}`} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{r.name}</div>
        <div className="text-xs text-muted-foreground truncate">{type === "worker" ? r.qualification : r.type}
          {!r.available && r.conflicts[0] && <span className="text-red-300"> · {r.conflicts[0].message}</span>}</div>
      </div>
      <StatusBadge r={r} type={type} />
    </div>
  );
}

export const SITE_FILTERS = [
  { key: "site_type", label: "Tipologia cantiere", all: "Tutte le tipologie", name: (v) => v, testid: "plan-site-type-filter" },
  { key: "status", label: "Stato cantiere", all: "Tutti gli stati", name: (v) => SITE_STATUS[v]?.label || v, testid: "plan-site-status-filter" },
];
export const NO_FILTERS = { site_type: "", status: "" };
const matchesFilters = (filters) => (s) => SITE_FILTERS.every(({ key }) => !filters[key] || s[key] === filters[key]);

export function SiteFilter({ cfg, sites, value, onChange, testidPrefix = "" }) {
  const values = [...new Set(sites.map((s) => s[cfg.key]).filter(Boolean))].sort((a, b) => cfg.name(a).localeCompare(cfg.name(b), "it"));
  return (
    <Field label={cfg.label}>
      <NativeSelect value={value} onChange={(e) => onChange(cfg.key, e.target.value)} data-testid={`${testidPrefix}${cfg.testid}`}>
        <option value="">{cfg.all} ({sites.length})</option>
        {values.map((v) => <option key={v} value={v}>{cfg.name(v)} ({sites.filter((s) => s[cfg.key] === v).length})</option>)}
      </NativeSelect>
    </Field>
  );
}

export function PeriodPanel({ sites, form, setField, site, days }) {
  const on = (k) => (e) => setField(k, e.target.value);
  const [filters, setFilters] = useState(NO_FILTERS);
  const shown = sites.filter(matchesFilters(filters));
  const changeFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    const list = sites.filter(matchesFilters(next));
    if (list.length && !list.some((s) => String(s.id) === String(form.siteId))) setField("siteId", String(list[0].id));
  };
  return (
    <div className="surface p-5 space-y-4">
      <div className="eyebrow flex items-center gap-2"><CalendarRange className="h-4 w-4 text-amber-400" />1 · Cantiere e periodo</div>
      <div className="grid grid-cols-2 gap-3">
        {SITE_FILTERS.map((cfg) => <SiteFilter key={cfg.key} cfg={cfg} sites={sites} value={filters[cfg.key]} onChange={changeFilter} />)}
      </div>
      {!shown.length && <div className="text-xs text-amber-300" data-testid="plan-site-filter-empty">Nessun cantiere con questi filtri: resta selezionato {site ? `${site.code} · ${site.name}` : "il cantiere precedente"}.</div>}
      <Field label="Cantiere">
        <NativeSelect value={form.siteId} onChange={on("siteId")} className="h-12 text-base" data-testid="plan-site-select">
          {(shown.length ? shown : sites.filter((s) => String(s.id) === String(form.siteId))).map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
        </NativeSelect>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Data iniziale"><Input type="date" className="h-12" value={form.start} onChange={on("start")} data-testid="plan-start-date" /></Field>
        <Field label="Data finale"><Input type="date" className="h-12" min={form.start} value={form.end} onChange={on("end")} data-testid="plan-end-date" /></Field>
      </div>
      <ShiftFields value={form.shift} onChange={(v) => setField("shift", v)} testid="plan-shift" big />
      {site && <div className="text-xs text-muted-foreground">Responsabile: <span className="text-foreground">{site.manager_name}</span> · {days ?? "…"} giorni lavorativi</div>}
    </div>
  );
}

export function SummaryCards({ summary }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="plan-summary">
      <div className="surface p-4"><div className="eyebrow">Operai disponibili</div><div className="num text-2xl font-semibold text-emerald-300 mt-1">{summary.workers_available}<span className="text-sm text-muted-foreground">/{summary.workers_total}</span></div></div>
      <div className="surface p-4"><div className="eyebrow">Mezzi disponibili</div><div className="num text-2xl font-semibold text-emerald-300 mt-1">{summary.vehicles_available}<span className="text-sm text-muted-foreground">/{summary.vehicles_total}</span></div></div>
    </div>
  );
}

export function ExistingList({ items, onDelete, onEdit }) {
  return (
    <div className="surface p-5">
      <div className="eyebrow mb-3">Già assegnati nel periodo</div>
      {!items.length ? <div className="text-sm text-muted-foreground">Nessuna assegnazione su questo cantiere nel periodo.</div> : (
        <ul className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin" data-testid="plan-existing-list">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm">
              {a.resource_type === "worker" ? <HardHat className="h-4 w-4 text-amber-400 shrink-0" /> : <Truck className="h-4 w-4 text-sky-400 shrink-0" />}
              <div className="flex-1 min-w-0"><div className="truncate">{a.resource_name}</div><div className="text-[11px] text-muted-foreground num">{fmtDate(a.start_date)} → {fmtDate(a.end_date)} · {a.shift}</div></div>
              <EditTimeButton onClick={() => onEdit(a)} testid={`plan-existing-time-${a.id}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => onDelete(a)} data-testid={`plan-existing-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ForceSwitch({ force, setForce }) {
  return (
    <label className="surface p-4 flex items-start gap-3 text-sm cursor-pointer border-dashed">
      <Switch checked={force} onCheckedChange={setForce} data-testid="plan-force-switch" />
      <span><span className="flex items-center gap-1 font-medium"><ShieldAlert className="h-4 w-4 text-red-400" />Test controllo server</span>
        <span className="text-xs text-muted-foreground">Consente di selezionare risorse non disponibili per dimostrare che il backend blocca comunque il salvataggio.</span></span>
    </label>
  );
}

export function ResourceToolbar({ tab, setTab, selW, selV, filter, setFilter, onSelectAll }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="worker" data-testid="plan-tab-workers"><HardHat className="h-4 w-4 mr-1.5" />Operai {selW.size > 0 && `(${selW.size})`}</TabsTrigger>
          <TabsTrigger value="vehicle" data-testid="plan-tab-vehicles"><Truck className="h-4 w-4 mr-1.5" />Mezzi {selV.size > 0 && `(${selV.size})`}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="pl-8 h-9" placeholder="Filtra…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="plan-filter-input" /></div>
        <Button size="sm" variant="outline" onClick={onSelectAll} data-testid="plan-select-all-btn"><CheckSquare className="h-4 w-4 mr-1" />Tutti disponibili</Button>
      </div>
    </div>
  );
}

export function ResourceGrid({ tab, valid, loading, list, sorted, selected, force, onToggle, onShowConflict }) {
  return (
    <ListState valid={valid} loading={loading} empty={!list.length}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid={`plan-${tab}-list`}>
        {sorted.map((r) => (
          <ResourceRow key={r.id} r={r} type={tab} force={force} checked={selected.has(r.id)} onToggle={onToggle} onShowConflict={onShowConflict} />
        ))}
      </div>
    </ListState>
  );
}

export function AssignBar({ selW, selV, estimate, disabled, busy, onAssign }) {
  return (
    <div className="sticky bottom-20 md:bottom-4 mt-5 z-10">
      <div className="rounded-xl border border-amber-500/50 bg-[#141a26]/95 backdrop-blur-md p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-2xl">
        <div className="flex-1 text-sm">
          <span className="font-semibold" data-testid="plan-selected-count">{selW.size} operai · {selV.size} mezzi selezionati</span>
          <span className="text-muted-foreground"> · costo stimato </span><span className="num text-amber-300 font-semibold" data-testid="plan-estimate">{eur(estimate)}</span>
        </div>
        <Button disabled={disabled} onClick={onAssign} className="h-12 px-6 text-base font-extrabold tracking-wide" data-testid="assign-site-button">
          {busy && <Loader2 className="h-5 w-5 animate-spin mr-2" />}ASSEGNA AL CANTIERE
        </Button>
      </div>
    </div>
  );
}
